// ═══════════════════════════════════════════════════════════════
// frontend/src/App.tsx — App with routing
// ═══════════════════════════════════════════════════════════════

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthContext, type User } from "./hooks/useAuth";
import { useState } from "react";
import Layout from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import PartnerList from "./pages/PartnerList";
import PartnerForm from "./pages/PartnerForm";
import ProductList from "./pages/ProductList";
import ProductForm from "./pages/ProductForm";
import CrmPipeline from "./pages/CrmPipeline";
import CrmForm from "./pages/CrmForm";
import SaleOrderList from "./pages/SaleOrderList";
import SaleOrderForm from "./pages/SaleOrderForm";
import InvoiceList from "./pages/InvoiceList";
import InvoiceForm from "./pages/InvoiceForm";
import DeliveryList from "./pages/DeliveryList";
import DeliveryForm from "./pages/DeliveryForm";
import InventoryOverview from "./pages/InventoryOverview";
import PurchaseList from "./pages/PurchaseList";
import PurchaseForm from "./pages/PurchaseForm";
import EmployeeList from "./pages/EmployeeList";
import MrpList from "./pages/MrpList";
import ProjectList from "./pages/ProjectList";
import TaskBoard from "./pages/TaskBoard";
import ExpenseList from "./pages/ExpenseList";
import Storefront from "./pages/Storefront";
import PosPage from "./pages/PosPage";
import HelpdeskList from "./pages/HelpdeskList";
import CampaignList from "./pages/CampaignList";
import SubscriptionPage from "./pages/SubscriptionPage";
import Login from "./pages/Login";

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 10_000 } },
});

export default function App() {
  const [user, setUser] = useState<User | null>({ id: 1, name: "Dev User", email: "dev@clouderp.local" });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={{
        user,
        loading: false,
        login: () => {},
        logout: () => { setUser(null); },
      }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/partners" element={<PartnerList />} />
              <Route path="/partners/new" element={<PartnerForm />} />
              <Route path="/partners/:id" element={<PartnerForm />} />
              <Route path="/products" element={<ProductList />} />
              <Route path="/products/new" element={<ProductForm />} />
              <Route path="/crm" element={<CrmPipeline />} />
              <Route path="/crm/new" element={<CrmForm />} />
              <Route path="/orders" element={<SaleOrderList />} />
              <Route path="/orders/new" element={<SaleOrderForm />} />
              <Route path="/invoices" element={<InvoiceList />} />
              <Route path="/invoices/new" element={<InvoiceForm />} />
              <Route path="/deliveries" element={<DeliveryList />} />
              <Route path="/deliveries/new" element={<DeliveryForm />} />
              <Route path="/inventory" element={<InventoryOverview />} />
              <Route path="/purchases" element={<PurchaseList />} />
              <Route path="/purchases/new" element={<PurchaseForm />} />
              <Route path="/employees" element={<EmployeeList />} />
              <Route path="/manufacturing" element={<MrpList />} />
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/tasks" element={<TaskBoard />} />
              <Route path="/expenses" element={<ExpenseList />} />
              <Route path="/store" element={<Storefront />} />
              <Route path="/pos" element={<PosPage />} />
              <Route path="/helpdesk" element={<HelpdeskList />} />
              <Route path="/campaigns" element={<CampaignList />} />
              <Route path="/subscriptions" element={<SubscriptionPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthContext.Provider>
    </QueryClientProvider>
  );
}
